import { useEffect, useMemo, useRef, useState } from "react";
import { VisualizationMode } from "./types";
import { VisualizationModeControls } from "./VisualizationModeSelector";
import { trackChangeTimeseriesMode } from "../../analytics";

// // // //

/**
 * Custom hook for managing visualization modes.
 *
 * @param modes An array of `VisualizationMode` objects to manage.
 * @returns An object containing the current active mode, the list of modes,
 *          and a function to change the active mode.
 *
 * @example
 * const { activeMode, modes, setActiveMode } = useMode(visualizationModes);
 */
export function useVisualizationModeState(
    modes: VisualizationMode[],
): VisualizationModeControls {
    // State to track the currently active visualization mode, defaults to the first mode.
    const [activeMode, setActiveMode] = useState<VisualizationMode>(modes[0]);

    // State to indicate whether the mode change is in progress (loading state).
    const [loading, setLoading] = useState(false);

    // Derive a stable key from mode keys to avoid infinite re-renders
    // when the modes array is recreated with the same contents on every render.
    const modesKey = useMemo(() => modes.map((m) => m.key).join(","), [modes]);

    // useEffect hook to change the activeMode when "modes" parameters changes
    useEffect(() => {
        setActiveMode(modes[0]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modesKey]);

    // useRef hook to maintain a stable reference to the function that updates the active mode.
    // This prevents unnecessary re-renders or effects executions that depend on this function.
    const changeMode = useRef((updatedMode: VisualizationMode) => {
        // Track change event
        trackChangeTimeseriesMode({
            prevValue: activeMode.key,
            nextValue: updatedMode.key,
        });

        setLoading(true); // Start loading before changing the mode.
        setActiveMode(updatedMode); // Update the active mode.
    });

    // useEffect hook to handle the loading state. It simulates an asynchronous operation like fetching data.
    useEffect(() => {
        // Set a timeout to simulate a loading period for the mode change, then set loading to false.
        const timeoutId = setTimeout(() => {
            setLoading(false); // Stop loading after a delay.
        }, 750);

        // Cleanup function to clear the timeout if the component unmounts or the active mode changes before the timeout completes.
        return () => {
            clearTimeout(timeoutId);
        };
    }, [activeMode]); // Dependency array: this effect runs every time the activeMode changes.

    // Return the current mode state, the list of modes, the loading state, and the function to change the active mode.
    return {
        activeMode, // Currently active mode.
        modes, // Available visualization modes.
        loading, // Indicates if the mode change is in progress.
        setActiveMode: changeMode.current, // Function to change the active mode.
    };
}
